import React, { useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import useClients from "./useClients";
import { sendNotification } from "./notify";

const FOLDERS = [
  { id:"bank",        name:"Bank Statements",         icon:"🏦" },
  { id:"tax",         name:"Tax Documents",            icon:"📋" },
  { id:"bookkeeping", name:"QBO / Bookkeeping",        icon:"📊" },
  { id:"payroll",     name:"Payroll Documents",        icon:"💼" },
  { id:"loans",       name:"Loan Documents",           icon:"🏛" },
  { id:"cfo",         name:"CFO Reports & Scorecards", icon:"📈" },
  { id:"corporate",   name:"Corporate Docs",           icon:"🏢" },
  { id:"checks",      name:"Checks & Receipts",        icon:"🧾" },
  { id:"forms1099",   name:"1099 / Tax Forms",         icon:"📑" },
  { id:"other",       name:"Other / Miscellaneous",    icon:"📎" },
  { id:"custom",      name:"+ Custom Category",        icon:"✏️" },
];

const S = {
  input:{width:"100%",padding:"7px 10px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  btn:{fontSize:12,padding:"6px 14px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"6px 14px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
};

export default function DocumentRequestModal({ client, initialClient, onClose, onSaved, currentUserName, isAdmin=true }) {
  const { clientNames } = useClients();
  const [selectedClient, setSelectedClient] = useState(client?.name || initialClient || "");
  const [folders, setFolders] = useState([]);
  const [customFolder, setCustomFolder] = useState("");
  const [notes, setNotes] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("High");
  const [saving, setSaving] = useState(false);

  const toggleFolder = (id) => {
    setFolders(prev => prev.includes(id) ? prev.filter(f=>f!==id) : [...prev, id]);
  };

  const handleSave = async () => {
    if(folders.length === 0){ alert("Please select at least one folder."); return; }
    if(folders.includes("custom") && !customFolder.trim()){ alert("Please enter a name for the custom category."); return; }
    if(!notes.trim()){ alert("Please add a note describing what you need."); return; }
    setSaving(true);

    if(!selectedClient){ alert("Please select a client."); setSaving(false); return; }
    const folderNames = folders.map(id => id==="custom" ? customFolder.trim() : FOLDERS.find(f=>f.id===id)?.name).filter(Boolean);
    const folderIcons = folders.map(id => FOLDERS.find(f=>f.id===id)?.icon).filter(Boolean);
    const title = `Document Request — ${folderNames.join(", ")}`;

    try {
      await addDoc(collection(db,"tasks"),{
        title,
        client: selectedClient,
        category: "Document Request",
        assignedTo: "",
        assignedBy: currentUserName || "Frank Ficsori",
        folderRef: folderNames[0], // Primary folder for the link
        folderRefs: folderNames,   // All folders
        folderIcons,
        notes: notes.trim(),
        priority,
        due: due || "",
        status: "To Do",
        isDocumentRequest: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      if(onSaved) onSaved(client?.name || selectedClient, folderNames);
      // Send email notification to client BEFORE closing modal
      try {
        const clientSnap = await getDocs(query(collection(db,"clients"), where("name","==",client?.name || selectedClient)));
        if(!clientSnap.empty){
          const clientData = clientSnap.docs[0].data();
          if(clientData.email){
            await sendNotification("document_request",{
              recipientEmail: clientData.email,
              recipientName: clientData.contact||clientData.name,
              senderName: currentUserName||"Frank Ficsori",
              clientName: client?.name || selectedClient,
              details: `Folders requested: ${folderNames.join(", ")}${notes.trim()?"<br><br>"+notes.trim():""}`,
              portalUrl: "https://portal.ficsoriaccounting.com/#tasks",
            });
          }
        }
      } catch(e){ console.error("Notification error:", e); }
      onClose();
      alert(`✓ Document request sent to ${client?.name || selectedClient}!\n\nThey will see it in their Action Items when they log into the portal.`);
    } catch(e){
      alert("Failed to save request: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#fff",borderRadius:16,width:540,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 8px 40px rgba(0,0,0,0.15)"}}>

        {/* Header */}
        <div style={{padding:"18px 22px",borderBottom:"0.5px solid #e0dfd8",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div>
            <div style={{fontSize:14,fontWeight:500}}>📄 Request Documents</div>
            <div style={{fontSize:12,color:"#888",marginTop:2}}>{selectedClient||"Select a client below"}</div>
          </div>
          <button style={{...S.btn,padding:"3px 10px"}} onClick={onClose}>✕</button>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}}>

          {/* Client selector - only show if no client pre-selected */}
          {(!client?.name) && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:"#888",fontWeight:500,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>Client *</div>
              <select style={S.input} value={selectedClient} onChange={e=>setSelectedClient(e.target.value)}>
                <option value="">Select client...</option>
                {clientNames.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          )}

          {/* Folder selection */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,color:"#888",fontWeight:500,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>
              Select document folders needed *
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
              {FOLDERS.map(f=>{
                const selected = folders.includes(f.id);
                return (
                  <div key={f.id} onClick={()=>toggleFolder(f.id)} style={{
                    display:"flex",alignItems:"center",gap:7,padding:"8px 10px",
                    borderRadius:8,border:"0.5px solid "+(selected?"#C0111F":"#e0dfd8"),
                    background:selected?"#fdf0f0":"#fafaf8",
                    cursor:"pointer",
                    boxShadow:selected?"0 0 0 1px #C0111F22":"none",
                  }}>
                    <span style={{fontSize:16}}>{f.icon}</span>
                    <span style={{fontSize:11,fontWeight:selected?500:400,color:selected?"#C0111F":"#555"}}>{f.name}</span>
                  </div>
                );
              })}
            </div>
            {folders.includes("custom")&&(
              <div style={{marginTop:8}}>
                <input style={S.input} value={customFolder} onChange={e=>setCustomFolder(e.target.value)} placeholder="e.g. Insurance Documents, HOA Records..." autoFocus/>
              </div>
            )}
            {folders.length>0&&(
              <div style={{marginTop:8,fontSize:11,color:"#C0111F"}}>
                {folders.length} folder{folders.length!==1?"s":""} selected: {folders.map(id=>FOLDERS.find(f=>f.id===id)?.name).join(", ")}
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:"#888",fontWeight:500,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>
              What do you need? *
            </div>
            <textarea
              style={{...S.input,resize:"vertical",minHeight:100,lineHeight:1.6}}
              value={notes}
              onChange={e=>setNotes(e.target.value)}
              placeholder="e.g. Please upload the most recent Bank of America, Amex, Costco, and Bank OZK statements so I can reconcile the accounts and close the month. If any statements aren't available yet, please let me know the expected date."
            />
          </div>

          {/* Due date + Priority */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <div style={{fontSize:11,color:"#888",fontWeight:500,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>Due date</div>
              <input style={S.input} type="date" value={due} onChange={e=>setDue(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:11,color:"#888",fontWeight:500,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>Priority</div>
              <select style={S.input} value={priority} onChange={e=>setPriority(e.target.value)}>
                <option>High</option>
                <option>Med</option>
                <option>Low</option>
              </select>
            </div>
          </div>

          {/* Info banner */}
          <div style={{marginTop:14,padding:"10px 14px",background:isAdmin?"#E6F1FB":"#FAEEDA",borderRadius:8,fontSize:12,color:isAdmin?"#185FA5":"#854F0B",lineHeight:1.6}}>
            {isAdmin
              ? <>ℹ️ This will create an Action Item in the <strong>client's portal</strong>. They will see your request and can respond or upload documents directly.</>
              : <>ℹ️ This request will be sent to <strong>Frank Ficsori</strong> as an internal task. Frank will review and provide the documents or forward the request to the client.</>
            }
          </div>
        </div>

        {/* Footer */}
        <div style={{padding:"14px 22px",borderTop:"0.5px solid #e0dfd8",display:"flex",gap:8,justifyContent:"flex-end",flexShrink:0}}>
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving?"Sending...":isAdmin?"📤 Send to Client":"📤 Send Request to Frank"}
          </button>
        </div>
      </div>
    </div>
  );
}
